import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { adminService } from '../services/adminService';
import api, { getApiBaseUrl, apiFetch } from '../services/api';
import { hkDateISO } from '../utils/dateUtils';
import { isValidEmail, findInvalidEmails } from '../utils/emailUtils';
import { copyTextToClipboard } from '../utils/clipboard.js';
import CopyFieldsModal from '../components/admin/CopyFieldsModal';
import { readImportResponse } from '../utils/importProgress';
import { newUuid } from '../utils/uuid.js';

const TEMPLATE_TABS = {
  library: 'library',
  links: 'links',
  assign: 'assign',
};

const EXPIRATION_OPTIONS = [
  { value: '', label: 'Never expires (default)' },
  { value: '24', label: '24 hours' },
  { value: '48', label: '48 hours' },
  { value: '72', label: '72 hours' },
  { value: '168', label: '1 week' },
  { value: '720', label: '30 days' },
];

const styles = {
  page: {
    minHeight: '100%',
    background: '#f0f2f5',
    padding: '6px 6px 30px',
  },
  card: {
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    padding: '20px',
    marginBottom: '30px',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    borderBottom: '1px solid #e9ecef',
    marginBottom: '20px',
    flexWrap: 'wrap',
    paddingBottom: '0',
  },
  tabs: {
    display: 'flex',
    gap: '0',
    flexWrap: 'wrap',
    marginBottom: '-1px',
  },
  tab: {
    padding: '10px 20px',
    cursor: 'pointer',
    borderBottomWidth: '3px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    color: '#6c757d',
    fontWeight: 500,
    fontSize: '14px',
    transition: 'all 0.2s',
    userSelect: 'none',
  },
  tabActive: {
    color: 'var(--company-primary-color, #273947)',
    borderBottomColor: 'var(--company-primary-color, #273947)',
  },
  tabHover: {
    color: '#333333',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '15px',
    flexWrap: 'wrap',
  },
  countLabel: {
    fontSize: '16px',
    color: '#6c757d',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  sectionTitleButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '16px',
    color: '#6c757d',
    fontWeight: 600,
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  actionGroup: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  primaryButton: {
    background: 'var(--company-primary-color, #273947)',
    color: 'var(--company-primary-contrast, #fff)',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '13px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    textDecoration: 'none',
  },
  successButton: {
    background: '#fff',
    color: 'var(--company-primary-color, #273947)',
    border: '1px solid #d9e0ea',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '13px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  outlineButton: {
    background: 'transparent',
    color: '#333',
    border: '1px solid #e9ecef',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '13px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  dangerButton: {
    background: 'transparent',
    color: '#dc3545',
    border: '1px solid #dc3545',
    borderRadius: '4px',
    padding: '5px 10px',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
  warningButton: {
    background: '#fff',
    color: '#856404',
    border: '1px solid #ffc107',
    borderRadius: '4px',
    padding: '5px 10px',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '12px',
  },
  smallPrimaryButton: {
    background: 'var(--company-primary-color, #273947)',
    color: 'var(--company-primary-contrast, #fff)',
    border: 'none',
    borderRadius: '4px',
    padding: '5px 10px',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  },
  smallOutlineButton: {
    background: 'transparent',
    color: '#333',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#e9ecef',
    borderRadius: '4px',
    padding: '5px 10px',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '20px',
  },
  tile: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#e9ecef',
    borderRadius: '8px',
    padding: '20px',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '180px',
    boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
    transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
    cursor: 'pointer',
  },
  tileHover: {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)',
    borderColor: '#4a90e2',
  },
  tileHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '15px',
  },
  tileTitle: {
    minWidth: 0,
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--company-primary-color, #273947)',
    lineHeight: 1.3,
    overflowWrap: 'anywhere',
  },
  tileMeta: {
    fontSize: '12px',
    color: '#6c757d',
    marginBottom: '20px',
    flex: 1,
  },
  tileActions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    borderTop: '1px solid #e9ecef',
    paddingTop: '15px',
  },
  badge: {
    display: 'inline-flex',
    flexShrink: 0,
    alignItems: 'center',
    borderRadius: '999px',
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  tableWrap: {
    overflowX: 'auto',
    background: '#fff',
    borderRadius: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '900px',
  },
  th: {
    textAlign: 'left',
    padding: '12px 15px',
    borderBottom: '2px solid #e9ecef',
    color: '#6c757d',
    fontSize: '13px',
    fontWeight: 600,
    textTransform: 'uppercase',
    background: '#fff',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 15px',
    borderBottom: '1px solid #e9ecef',
    fontSize: '14px',
    color: '#333',
    verticalAlign: 'top',
  },
  tableRowHover: {
    background: '#f8f9fa',
  },
  countText: {
    fontSize: '13px',
    color: '#6c757d',
    marginBottom: '10px',
  },
  contentCard: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    padding: '20px',
    marginBottom: '30px',
  },
  topInfoRow: {
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  toolbarGroup: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  searchInput: {
    border: '1px solid #d9e0ea',
    borderRadius: '6px',
    padding: '10px 12px',
    fontSize: '14px',
    color: '#333',
    background: '#ffffff',
    outline: 'none',
    minWidth: '260px',
  },
  filterSelect: {
    border: '1px solid #d9e0ea',
    borderRadius: '6px',
    padding: '10px 12px',
    fontSize: '14px',
    color: '#333',
    background: '#ffffff',
    outline: 'none',
    minWidth: '240px',
  },
  iconButtonLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  linkUrlCell: {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: 'monospace',
    fontSize: '12px',
    color: '#4a90e2',
  },
  assignCell: {
    textAlign: 'center',
  },
  matrixCheckbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  subtle: {
    fontSize: '12px',
    color: '#6c757d',
    fontWeight: 500,
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 20px',
    color: '#6b7280',
    fontSize: '14px',
  },
  loadingState: {
    textAlign: 'center',
    padding: '48px 20px',
    color: '#6b7280',
    fontSize: '14px',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px',
  },
  modal: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.22)',
    width: '100%',
    maxWidth: '540px',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '24px',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 700,
    color: '#0d8a8a',
  },
  closeButton: {
    border: 'none',
    background: 'transparent',
    fontSize: '24px',
    lineHeight: 1,
    cursor: 'pointer',
    color: '#6b7280',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box',
    minHeight: '88px',
    resize: 'vertical',
  },
  helper: {
    marginTop: '6px',
    color: '#6b7280',
    fontSize: '12px',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px',
    flexWrap: 'wrap',
  },
  dropZone: {
    borderWidth: '2px',
    borderStyle: 'dashed',
    borderColor: '#d1d5db',
    borderRadius: '12px',
    padding: '40px 24px',
    textAlign: 'center',
    cursor: 'pointer',
    background: '#f9fafb',
    marginBottom: '16px',
  },
  dropZoneActive: {
    borderColor: '#0d8a8a',
    background: '#ecfeff',
  },
  progressBarWrap: {
    width: '100%',
    background: '#e5e7eb',
    borderRadius: '999px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  progressBar: {
    height: '8px',
    background: '#0d8a8a',
    transition: 'width 0.3s ease',
  },
  resultBox: {
    marginTop: '16px',
    padding: '16px',
    background: '#f8fafc',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
  },
  resultRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '10px',
  },
  monoInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontFamily: 'monospace',
    fontSize: '12px',
    background: '#fff',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#374151',
    cursor: 'pointer',
  },
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).replace(',', '');
}

function getFormName(form) {
  return form?.name || form?.title || form?.formName || 'Untitled Form';
}

function isPublished(form) {
  return Boolean(form?.isPublished || form?.published || form?.status === 'published');
}

function getStatusBadgeStyle(type) {
  const base = styles.badge;
  switch (type) {
    case 'draft':
      return { ...base, background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db' };
    case 'published':
      return { ...base, background: '#dcfce7', color: '#166534' };
    case 'active':
      return { ...base, background: '#dcfce7', color: '#166534' };
    case 'expired':
      return { ...base, background: '#fee2e2', color: '#991b1b' };
    case 'used':
      return { ...base, background: '#e5e7eb', color: '#374151' };
    default:
      return { ...base, background: '#e0f2fe', color: '#075985' };
  }
}

function getLinkStatus(link) {
  if (link?.isExpired) return { text: 'Expired', type: 'expired' };
  if (link?.isUsed && !(link?.isReusable ?? link?.reusable)) return { text: 'Used', type: 'used' };
  return { text: 'Active', type: 'active' };
}

function buildFormUrl(formId, formName, { skipConsent = false } = {}) {
  const lower = (formName || '').toLowerCase();
  if (lower.includes('onboarding') || lower.includes('registration')) {
    return skipConsent ? '/onboarding?skipConsent=1&internal=1' : '/onboarding?internal=1';
  }
  return `/form/${encodeURIComponent(formId)}?internal=1`;
}

export default function AdminPortalPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert, showConfirm } = useModal();

  const [activeTab, setActiveTab] = useState(TEMPLATE_TABS.library);

  // A platform owner does not own templates: its job is to administer which company can
  // see which template, so that is the page it lands on. A company admin lands on its own
  // library. Applied once per signed-in user because the role arrives asynchronously -
  // and re-applying it on every render would drag a deliberate tab click back.
  const activeTabDefaultedFor = useRef(null);
  useEffect(() => {
    const identity = user?.id || user?.username;
    if (!identity || activeTabDefaultedFor.current === identity) return;
    activeTabDefaultedFor.current = identity;
    setActiveTab(user?.role === 'superadmin' ? TEMPLATE_TABS.assign : TEMPLATE_TABS.library);
  }, [user]);
  const [forms, setForms] = useState([]);
  const [links, setLinks] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSmartImport, setIsSmartImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importItems, setImportItems] = useState([]);
  const [importElapsed, setImportElapsed] = useState(0);
  useEffect(() => {
    if (!importing) return;
    const started = Date.now();
    setImportElapsed(0);
    const timer = setInterval(() => setImportElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [importing]);
  const [collapsedSections, setCollapsedSections] = useState({ draft: false, published: false, builtin: false });
  const [generateResult, setGenerateResult] = useState(null);
  const [previewForm, setPreviewForm] = useState(null);
  const [generateSubmitting, setGenerateSubmitting] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [renameFormState, setRenameFormState] = useState({ id: '', name: '' });
  // Source template while the Copy Fields picker is open; null when closed.
  const [copyFieldsSource, setCopyFieldsSource] = useState(null);
  const [generateForm, setGenerateForm] = useState({
    formId: '',
    doctorEmail: '',
    ccEmail: '',
    bccEmail: '',
    expiryDate: '',
    neverExpired: false,
    linkType: 'reusable',
    includeHeidiConsent: false,
  });
  // Pre-fill: answers staff typed into the real form, attached to the link so the
  // patient does not have to retype them.
  const [prefillValues, setPrefillValues] = useState({});

  const fileInputRef = useRef(null);
  const [hoveredTab, setHoveredTab] = useState(null);
  const [hoveredTileId, setHoveredTileId] = useState(null);
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  const [hoveredButtonKey, setHoveredButtonKey] = useState(null);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkFormFilter, setLinkFormFilter] = useState('all');

  const publishedForms = useMemo(() => forms.filter((form) => isPublished(form)), [forms]);
  const draftForms = useMemo(() => forms.filter((form) => !isPublished(form)), [forms]);

  const loadForms = useCallback(async () => {
    const data = await adminService.getAllForms();
    setForms(Array.isArray(data) ? data : []);
  }, []);

  const loadLinks = useCallback(async () => {
    const data = await adminService.getAllLinks();
    setLinks(Array.isArray(data) ? data : []);
  }, []);

  const loadClinics = useCallback(async () => {
    const data = await adminService.getAllClinics();
    return Array.isArray(data) ? data : [];
  }, []);

  const loadDoctors = useCallback(async () => {
    try {
      const data = await adminService.getAllDoctors();
      setDoctors(Array.isArray(data) ? data : []);
    } catch {
      setDoctors([]);
    }
  }, []);

  const loadAssignments = useCallback(async (clinicList) => {
    const apiBase = getApiBaseUrl();
    try {
      const response = await apiFetch(`${apiBase}/clinics/assignments`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        // Merge with empty arrays for clinics that have no assignments yet
        const merged = {};
        clinicList.forEach((clinic) => {
          merged[clinic.id] = Array.isArray(data[clinic.id]) ? data[clinic.id] : [];
        });
        setAssignments(merged);
        return;
      }
    } catch {
      // fall through to empty
    }
    // Fallback: empty assignments
    const results = clinicList.map((clinic) => [clinic.id, []]);
    setAssignments(Object.fromEntries(results));
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const clinicList = await loadClinics();
      setClinics(clinicList);
      await Promise.all([loadForms(), loadLinks(), loadDoctors(), loadAssignments(clinicList)]);
    } catch (error) {
      await showAlert(error?.message || 'Failed to load admin portal data.', 'Load Error');
    } finally {
      setLoading(false);
    }
  }, [loadAssignments, loadClinics, loadDoctors, loadForms, loadLinks, showAlert]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const doctorEmails = useMemo(() => {
    return doctors
      .map((doctor) => doctor?.email)
      .filter(Boolean)
      .filter((value, index, array) => array.indexOf(value) === index);
  }, [doctors]);

  const clinicMap = useMemo(() => {
    return Object.fromEntries(clinics.map((clinic) => [clinic.id, clinic]));
  }, [clinics]);

  // Built from the links themselves rather than the template list: a link can outlive
  // the template it was generated from, and a template with no links would otherwise
  // sit in the dropdown selecting nothing.
  const linkFormOptions = useMemo(() => {
    const counts = {};
    links.forEach((link) => {
      const key = link.formName || 'Unknown';
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, count]) => ({ name, count }));
  }, [links]);

  // Searches the same values the table shows, plus the full URL and token: the URL
  // column is truncated to a path, so pasting a full link a colleague sent must still
  // find its row.
  const visibleLinks = useMemo(() => {
    const term = linkSearch.trim().toLowerCase();
    return links.filter((link) => {
      const matchesForm = linkFormFilter === 'all' || (link.formName || 'Unknown') === linkFormFilter;
      if (!matchesForm) return false;
      if (!term) return true;
      const clinic = clinicMap[link?.clinicId];
      return [
        link.formName,
        link.clinicName,
        clinic?.name,
        clinic?.code,
        link.doctorEmail,
        link.patientName,
        link.url,
        link.token,
        link.notes,
        getLinkStatus(link).text,
      ].some((value) => (value || '').toString().toLowerCase().includes(term));
    });
  }, [links, linkSearch, linkFormFilter, clinicMap]);

  const openImportModal = (smart) => {
    setIsSmartImport(Boolean(smart));
    setSelectedFiles([]);
    setImporting(false);
    setImportProgress(0);
    setImportItems([]);
    setDragOver(false);
    setImportModalOpen(true);
  };

  const closeImportModal = () => {
    if (importing) return;
    setImportModalOpen(false);
  };

  const handleSelectFiles = (fileList) => {
    setSelectedFiles(Array.from(fileList || []));
    setImportItems([]);
    setImportProgress(0);
  };

  const uploadFiles = async () => {
    if (!selectedFiles.length) {
      await showAlert('Please select at least one file.', 'Import PDF');
      return;
    }

    const apiBase = getApiBaseUrl();

    setImporting(true);
    setImportProgress(0);
    setImportItems(selectedFiles.map(file => ({ name: file.name, status: 'queued', stage: 'Waiting' })));
    const updateItem = (index, patch) => setImportItems(items => items.map((item, i) => i === index ? { ...item, ...patch } : item));
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        // crypto.randomUUID is undefined outside a secure context, and the portal
        // runs over plain http on a LAN address. newUuid falls back safely.
        const progressId = newUuid();
        const formData = new FormData();
        formData.append('file', file);
        updateItem(i, { status: 'active', stage: 'Sending file / waiting for server' });
        try {
          const query = new URLSearchParams({ progressId, streamProgress: 'true' });
          if (isSmartImport) query.set('smartImport', 'true');
          const response = await apiFetch(`${apiBase}/admin/forms/upload?${query}`, {
            method: 'POST', credentials: 'include', body: formData,
          });
          const result = await readImportResponse(response, progress => updateItem(i, {
            stage: progress.stage, currentPage: progress.currentPage, totalPages: progress.totalPages, fieldsFound: progress.fieldsFound,
            warning: progress.status === 'failed' ? `${progress.stage} failed; checking remaining detection steps` : null,
          }));
          updateItem(i, { status: 'completed', stage: 'Draft saved', fieldsFound: result.pdfFieldCount, currentPage: null, totalPages: null, warning: null });
        } catch (error) {
          updateItem(i, { status: 'failed', stage: error?.message || 'Import failed', warning: null });
        } finally {
          setImportProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
        }
      }
      setSelectedFiles([]);
      await loadForms();
    } finally {
      setImporting(false);
    }
  };

  const resetGenerateModal = useCallback(() => {
    setGenerateForm({
      formId: '',
      doctorEmail: '',
      ccEmail: '',
      bccEmail: '',
      expiryDate: '',
      neverExpired: false,
      linkType: 'reusable',
      includeHeidiConsent: false,
    });
    setGenerateResult(null);
    setGenerateSubmitting(false);
    setPrefillValues({});
  }, []);

  // Coming back from "Fill Form First": reopen the dialog with the answers just
  // saved and the settings that were already entered, so the flow continues where
  // it left off instead of starting over.
  useEffect(() => {
    const raw = sessionStorage.getItem('prefillReturn');
    if (!raw) return;
    sessionStorage.removeItem('prefillReturn');

    try {
      const ctx = JSON.parse(raw);
      const saved = sessionStorage.getItem('prefillValues');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Answers are stored against the form they were typed into. Ignore (and
        // clear) anything belonging to a different template, otherwise switching
        // templates would carry the previous form's answers across.
        if (parsed?.formId === ctx.formId) {
          setPrefillValues(parsed.values || {});
        } else {
          sessionStorage.removeItem('prefillValues');
        }
      }

      setGenerateForm({
        formId: ctx.formId || '',
        doctorEmail: ctx.doctorEmail || '',
        ccEmail: ctx.ccEmail || '',
        bccEmail: ctx.bccEmail || '',
        expiryDate: ctx.expiryDate || '',
        neverExpired: !!ctx.neverExpired,
        linkType: ctx.linkType || 'reusable',
        includeHeidiConsent: false,
      });
      setGenerateResult(null);
      setGenerateSubmitting(false);
      setGenerateModalOpen(true);
    } catch {
      // A malformed context just means no auto-reopen; nothing is lost.
      sessionStorage.removeItem('prefillValues');
    }
  }, []);

  // Number of pre-filled answers currently attached to this link.
  const prefillCount = Object.values(prefillValues)
    .filter((v) => String(v ?? '').trim() !== '').length;

  // Opens the real form so staff can fill it in. The form is told to come back
  // here on save (prefillReturn) instead of creating a draft, so the answers end
  // up on the link we are about to generate rather than in My Submissions.
  const handleFillFormFirst = (mode = 'new') => {
    if (!generateForm.formId || generateForm.formId === '__heidi_consent') return;
    // "new" means start from a blank form; "edit" keeps what was entered before.
    if (mode === 'new') sessionStorage.removeItem('prefillValues');
    sessionStorage.setItem('prefillReturn', JSON.stringify({
      formId: generateForm.formId,
      returnPath: window.location.pathname,
      doctorEmail: generateForm.doctorEmail,
      ccEmail: generateForm.ccEmail,
      bccEmail: generateForm.bccEmail,
      expiryDate: generateForm.expiryDate,
      neverExpired: generateForm.neverExpired,
      linkType: generateForm.linkType,
      includeHeidiConsent: generateForm.includeHeidiConsent,
    }));
    navigate(`/form/${encodeURIComponent(generateForm.formId)}?internal=1&prefill=1`);
  };

  const clearPrefill = () => {
    setPrefillValues({});
    sessionStorage.removeItem('prefillValues');
  };

  const openGenerateModal = useCallback(
    (formId = '') => {
      resetGenerateModal();
      setGenerateForm((prev) => ({ ...prev, formId }));
      setGenerateModalOpen(true);
    },
    [resetGenerateModal]
  );

  const closeGenerateModal = () => {
    if (generateSubmitting) return;
    setGenerateModalOpen(false);
    setPrefillValues({});
    // Abandoning the dialog discards the pre-fill too, so reopening it later does
    // not resurrect answers the user chose not to use.
    sessionStorage.removeItem('prefillValues');
    sessionStorage.removeItem('prefillReturn');
  };

  const handleGenerateLink = async () => {
    if (!generateForm.formId) {
      await showAlert('Please select a template.', 'Generate Link');
      return;
    }
    if (!generateForm.doctorEmail.trim()) {
      await showAlert('Form Recipient Email is required.', 'Generate Link');
      return;
    }
    // The Heidi consent link reuses these two fields as patient name and notes
    // (see the createConsentLink call below), so they are not addresses there.
    const validatesAsEmail = generateForm.formId !== '__heidi_consent';
    if (validatesAsEmail && !isValidEmail(generateForm.doctorEmail)) {
      await showAlert('Form Recipient Email is not a valid email address.', 'Generate Link');
      return;
    }
    if (validatesAsEmail) {
      // CC is optional but accepts a comma-separated list, so every entry is checked.
      const invalidCc = findInvalidEmails(generateForm.ccEmail);
      if (invalidCc.length > 0) {
        await showAlert(`Invalid CC email: ${invalidCc.join(', ')}`, 'Generate Link');
        return;
      }

      const invalidBcc = findInvalidEmails(generateForm.bccEmail);
      if (invalidBcc.length > 0) {
        await showAlert(`Invalid BCC email: ${invalidBcc.join(', ')}`, 'Generate Link');
        return;
      }
    }
    if (!generateForm.neverExpired && !generateForm.expiryDate) {
      await showAlert('Link Expiry Date is required.', 'Generate Link');
      return;
    }
    // `min` only constrains the picker; a typed or pasted date still gets through,
    // and a past date would otherwise be clamped to the 1-hour floor below and
    // produce a link that expires almost immediately.
    if (!generateForm.neverExpired && generateForm.expiryDate < hkDateISO()) {
      await showAlert('Link Expiry Date cannot be in the past.', 'Generate Link');
      return;
    }

    setGenerateSubmitting(true);
    try {
      let expirationHours = null;
      if (!generateForm.neverExpired && generateForm.expiryDate) {
        const expiryMs = new Date(generateForm.expiryDate).getTime() - Date.now();
        expirationHours = Math.max(1, Math.ceil(expiryMs / 3600000));
      }

      let result;
      if (generateForm.formId === '__heidi_consent') {
        result = await adminService.createConsentLink({
          patientName: generateForm.doctorEmail.trim(),
          expirationHours,
          notes: generateForm.ccEmail.trim() || null,
        });
      } else {
        // Only send fields staff actually filled in; blanks are dropped so they
        // never overwrite a field with an empty value.
        const cleanedPrefill = Object.fromEntries(
          Object.entries(prefillValues).filter(([, v]) => String(v ?? '').trim() !== '')
        );
        result = await adminService.createLink({
          formId: generateForm.formId,
          doctorEmail: generateForm.doctorEmail.trim(),
          ccEmail: generateForm.ccEmail.trim() || null,
          bccEmail: generateForm.bccEmail.trim() || null,
          expirationHours,
          reusable: generateForm.linkType === 'reusable',
          skipHeidiConsent: !generateForm.includeHeidiConsent,
          ...(Object.keys(cleanedPrefill).length > 0
            ? { prefillFieldValues: cleanedPrefill }
            : {}),
        });
      }

      setGenerateResult(result);
      // The answers now live on the generated link; keeping the local copy would
      // silently pre-fill the next link the user creates.
      setPrefillValues({});
      sessionStorage.removeItem('prefillValues');
      await loadLinks();
    } catch (error) {
      await showAlert(error?.response?.data?.message || error?.message || 'Failed to generate link.', 'Generate Link');
    } finally {
      setGenerateSubmitting(false);
    }
  };

  const handleDeleteLink = async (token) => {
    const confirmed = await showConfirm('Are you sure you want to delete this link?', {
      title: 'Delete Link',
      okText: 'Delete',
      cancelText: 'Cancel',
      danger: true,
    });
    if (!confirmed) return;

    try {
      await adminService.deleteLink(token);
      await loadLinks();
    } catch (error) {
      await showAlert(error?.response?.data?.message || error?.message || 'Failed to delete link.', 'Delete Link');
    }
  };

  const handleDeleteForm = async (formId) => {
    const confirmed = await showConfirm('Are you sure you want to delete this template?', {
      title: 'Delete Template',
      okText: 'Delete',
      cancelText: 'Cancel',
      danger: true,
    });
    if (!confirmed) return;

    try {
      await adminService.deleteForm(formId);
      await loadForms();
      await loadAssignments(clinics);
    } catch (error) {
      await showAlert(error?.response?.data?.message || error?.message || 'Failed to delete template.', 'Delete Template');
    }
  };

  // ---- COPY FIELDS ----
  // Copies one template's entire field configuration onto another, replacing whatever
  // the target had. Bounds are absolute PDF points and are reused as-is, so the result
  // only lines up when both PDFs share the same page size and page count; the modal
  // warns about the overwrite, and the operator is expected to know the geometry
  // matches. Deliberately not validated here - page geometry is not exposed to the
  // client, and guessing would either block valid copies or give false assurance.
  const handleCopyFields = async (sourceFormId, targetFormId, targetName) => {
    setCopyFieldsSource(null);
    try {
      const res = await api.post(
        `/forms/${encodeURIComponent(sourceFormId)}/copy-fields-to/${encodeURIComponent(targetFormId)}`
      );
      await loadForms();
      await showAlert(res?.data?.message || `Fields copied to "${targetName}".`, 'Copy Fields');
    } catch (error) {
      await showAlert(
        error?.response?.data?.error || error?.message || 'Failed to copy fields.',
        'Copy Fields'
      );
    }
  };

  const openRenameModal = (form) => {
    setRenameFormState({ id: form.id, name: getFormName(form) });
    setRenameSubmitting(false);
    setRenameModalOpen(true);
  };

  const closeRenameModal = () => {
    if (renameSubmitting) return;
    setRenameModalOpen(false);
  };

  const handleRenameForm = async () => {
    const nextName = renameFormState.name.trim();
    if (!nextName) {
      await showAlert('Please enter a template name.', 'Rename Template');
      return;
    }

    setRenameSubmitting(true);
    try {
      await adminService.renameForm(renameFormState.id, nextName);
      setRenameModalOpen(false);
      await loadForms();
      await showAlert('Template renamed successfully.', 'Rename Template');
    } catch (error) {
      await showAlert(error?.response?.data?.message || error?.message || 'Failed to rename template.', 'Rename Template');
    } finally {
      setRenameSubmitting(false);
    }
  };

  const handleTogglePublish = async (form) => {
    const nextStatus = isPublished(form) ? 'draft' : 'published';
    const title = nextStatus === 'published' ? 'Publish Template' : 'Unpublish Template';
    const actionText = nextStatus === 'published' ? 'publish' : 'unpublish';

    // A form can only be published with a Primary Field set (it names the row in My
    // Submissions and lets drafts be found again). The backend enforces this too;
    // this pre-flight check just gives a clearer message than a failed request.
    // The Heidi consent tile is a pseudo-form with no configuration, so skip it.
    if (nextStatus === 'published' && form.id !== '__heidi_consent') {
      try {
        const res = await api.get(`/admin/forms/${form.id}/configuration`);
        if (!res.data?.primaryFieldName) {
          await showAlert(
            'This form has no Primary Field set. Open the Field Editor and set one under Primary Field before publishing.',
            title
          );
          return;
        }
      } catch (err) {
        // 404 = no configuration file at all, which also means no primary field.
        if (err?.response?.status === 404) {
          await showAlert(
            'This form has no field configuration yet. Open the Field Editor and set a Primary Field before publishing.',
            title
          );
          return;
        }
        // Any other error: fall through and let the publish request report it.
      }
    }

    const confirmed = await showConfirm(`Are you sure you want to ${actionText} "${getFormName(form)}"?`, {
      title,
      okText: nextStatus === 'published' ? 'Publish' : 'Unpublish',
      cancelText: 'Cancel',
    });

    if (!confirmed) return;

    try {
      await adminService.setFormStatus(form.id, nextStatus);
      await loadForms();
      await loadAssignments(clinics);
    } catch (error) {
      await showAlert(error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to update template status.', title);
    }
  };

  const handleToggleAssignment = (clinicId, formId) => {
    setAssignments((prev) => {
      const current = new Set(prev[clinicId] || []);
      if (current.has(formId)) current.delete(formId);
      else current.add(formId);
      return {
        ...prev,
        [clinicId]: Array.from(current),
      };
    });
  };

  // Ownership and assignment are different: assigning a template to a company lets that
  // company see it, but a company that is only assigned still gets 403 when it tries to
  // edit it. Ownership is what makes a template usable by the company that has to fill it
  // in, and it is the platform owner's to set.
  const handleChangeOwner = async (form, clinicId) => {
    try {
      // folderId is passed through because the move endpoint writes both fields, so
      // omitting it would drop the template out of its folder.
      await adminService.moveForm(form.id, clinicId || null, form.folderId || null);
      await loadForms();
    } catch (error) {
      await showAlert(
        error?.response?.data?.error || error?.message || 'Failed to change the owner.',
        'Change Owner'
      );
    }
  };

  const saveAssignments = async () => {
    setSavingAssignments(true);
    try {
      const apiBase = getApiBaseUrl();
      const response = await apiFetch(`${apiBase}/clinics/assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(assignments),
      });
      if (!response.ok) {
        throw new Error('Failed to save assignments');
      }
      await showAlert('Assignments saved successfully.', 'Assign to Clinic');
    } catch (error) {
      await showAlert(error?.message || 'Failed to save assignments.', 'Assign to Clinic');
    } finally {
      setSavingAssignments(false);
    }
  };

  const toggleSection = (key) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const templateOptions = useMemo(() => {
    return forms.map((form) => ({ id: form.id, name: getFormName(form) }));
  }, [forms]);

  const selectedGenerateForm = useMemo(
    () => {
      if (generateForm.formId === '__heidi_consent') {
        return { id: '__heidi_consent', name: 'Heidi Consent', displayName: 'Heidi Consent' };
      }
      return forms.find((form) => form.id === generateForm.formId);
    },
    [forms, generateForm.formId]
  );

  const getButtonHoverStyle = useCallback((key, hoverStyle) => {
    return hoveredButtonKey === key ? hoverStyle : null;
  }, [hoveredButtonKey]);

  // Anything that is not the platform owner is a company user: companyadmin or staff.
  const isPlatformOwner = user?.role === 'superadmin';

  if (loading) {
    return <div style={styles.loadingState}>Loading admin portal…</div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.topBar}>
          <div style={styles.tabs}>
            {!isPlatformOwner && (
              <div
                style={{ ...styles.tab, ...(activeTab === TEMPLATE_TABS.library ? styles.tabActive : {}) }}
                onClick={() => setActiveTab(TEMPLATE_TABS.library)}
              >
                Template Library
              </div>
            )}
            <div
              style={{ ...styles.tab, ...(activeTab === TEMPLATE_TABS.links ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(TEMPLATE_TABS.links)}
            >
              Link Management
            </div>
            {isPlatformOwner && (
              <div
                style={{ ...styles.tab, ...(activeTab === TEMPLATE_TABS.assign ? styles.tabActive : {}) }}
                onClick={() => setActiveTab(TEMPLATE_TABS.assign)}
              >
                Assign to Clinic
              </div>
            )}
          </div>
          <div style={styles.subtle}>{user?.displayName || user?.name || user?.username || 'Admin User'}</div>
        </div>

        {!isPlatformOwner && activeTab === TEMPLATE_TABS.library && (
          <>
            <div style={{ marginBottom: '40px' }}>
              <div style={styles.sectionHeader}>
                <button style={styles.sectionTitleButton} onClick={() => toggleSection('draft')}>
                  <span>{collapsedSections.draft ? '▶' : '▼'}</span>
                  <span>Draft Templates ({draftForms.length})</span>
                </button>
                <div style={styles.actionGroup}>
                  <button style={styles.primaryButton} onClick={() => openImportModal(false)}>
                    Import PDF
                  </button>
                  <button style={styles.successButton} onClick={() => openImportModal(true)}>
                    Smart Import (AI)
                  </button>
                </div>
              </div>

              {!collapsedSections.draft && (
                <div style={styles.grid}>
                  {draftForms.map((form) => (
                      <div
                        key={form.id}
                        style={{ ...styles.tile, ...(hoveredTileId === form.id ? styles.tileHover : {}) }}
                        onMouseEnter={() => setHoveredTileId(form.id)}
                        onMouseLeave={() => setHoveredTileId(null)}
                      >
                        <div style={styles.tileHeader}>
                          <h3 style={styles.tileTitle}>{getFormName(form)}</h3>
                          <span style={getStatusBadgeStyle('draft')}>Draft</span>
                        </div>
                        <div style={styles.tileMeta}>
                          Last Modified: {formatDate(form.updatedAt || form.modifiedAt || form.createdAt)}
                        </div>
                        <div style={styles.tileActions}>
                          {!((getFormName(form) || '').toLowerCase().includes('onboarding') || (getFormName(form) || '').toLowerCase().includes('registration')) && (
                            <button
                              style={{ ...styles.smallPrimaryButton, ...getButtonHoverStyle(`draft-edit-${form.id}`, { background: '#0a6270', transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(39, 55, 71, 0.28)' }) }}
                              onMouseEnter={() => setHoveredButtonKey(`draft-edit-${form.id}`)}
                              onMouseLeave={() => setHoveredButtonKey(null)}
                              onClick={() => navigate(`/admin/editor/${encodeURIComponent(form.id)}`)}
                            >
                              Edit
                            </button>
                          )}
                          <button
                            style={{ ...styles.smallOutlineButton, ...getButtonHoverStyle(`draft-rename-${form.id}`, { background: '#f5f7fa', borderColor: '#d6dde6', transform: 'translateY(-1px)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`draft-rename-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={() => openRenameModal(form)}
                          >
                            Rename
                          </button>
                          <button
                            style={{ ...styles.smallOutlineButton, ...getButtonHoverStyle(`draft-copyfields-${form.id}`, { background: '#f5f7fa', borderColor: '#d6dde6', transform: 'translateY(-1px)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`draft-copyfields-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={() => setCopyFieldsSource(form)}
                            title="Copy this template's field configuration onto another template"
                          >
                            Copy Fields
                          </button>
                          <button
                            style={{ ...styles.successButton, ...getButtonHoverStyle(`draft-publish-${form.id}`, { background: '#f8fafc', borderColor: 'var(--company-primary-color, #273947)', color: 'var(--company-primary-color, #273947)', transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(39, 55, 71, 0.12)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`draft-publish-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={() => handleTogglePublish(form)}
                          >
                            Publish
                          </button>
                          <button
                            style={{ ...styles.smallOutlineButton, ...getButtonHoverStyle(`draft-open-${form.id}`, { background: '#f5f7fa', borderColor: '#d6dde6', transform: 'translateY(-1px)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`draft-open-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={async () => {
                              const formName = getFormName(form);
                              const lower = (formName || '').toLowerCase();
                              const isRegistration = lower.includes('onboarding') || lower.includes('registration');
                                // Always skipped: this build has no consent step, so asking would
                                // lead to a route that does not exist.
                                const skipConsent = true;
                              sessionStorage.setItem('formReturnPath', window.location.pathname);
                              navigate(buildFormUrl(form.id, formName, { skipConsent }));
                            }}
                          >
                            Open Form
                          </button>
                          <button
                            style={{ ...styles.dangerButton, ...getButtonHoverStyle(`draft-delete-${form.id}`, { background: '#fff5f5', transform: 'translateY(-1px)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`draft-delete-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={() => handleDeleteForm(form.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div>
              <div style={styles.sectionHeader}>
                <button style={styles.sectionTitleButton} onClick={() => toggleSection('published')}>
                  <span>{collapsedSections.published ? '▶' : '▼'}</span>
                  <span>Published Templates ({publishedForms.length})</span>
                </button>
              </div>

              {!collapsedSections.published && (
                publishedForms.length ? (
                  <div style={styles.grid}>
                    {publishedForms.map((form) => (
                      <div
                        key={form.id}
                        style={{ ...styles.tile, ...(hoveredTileId === form.id ? styles.tileHover : {}) }}
                        onMouseEnter={() => setHoveredTileId(form.id)}
                        onMouseLeave={() => setHoveredTileId(null)}
                      >
                        <div style={styles.tileHeader}>
                          <h3 style={styles.tileTitle}>{getFormName(form)}</h3>
                          <span style={getStatusBadgeStyle('published')}>Published</span>
                        </div>
                        <div style={styles.tileMeta}>
                          Last Modified: {formatDate(form.updatedAt || form.modifiedAt || form.createdAt)}
                        </div>
                        <div style={styles.tileActions}>
                          <button
                            style={{ ...styles.smallPrimaryButton, ...getButtonHoverStyle(`published-link-${form.id}`, { background: '#0a6270', transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(39, 55, 71, 0.28)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`published-link-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={() => openGenerateModal(form.id)}
                          >
                            Generate Link
                          </button>
                          {!((getFormName(form) || '').toLowerCase().includes('onboarding') || (getFormName(form) || '').toLowerCase().includes('registration')) && (
                            <button
                              style={{ ...styles.smallOutlineButton, ...getButtonHoverStyle(`published-edit-${form.id}`, { background: '#f5f7fa', borderColor: '#d6dde6', transform: 'translateY(-1px)' }) }}
                              onMouseEnter={() => setHoveredButtonKey(`published-edit-${form.id}`)}
                              onMouseLeave={() => setHoveredButtonKey(null)}
                              onClick={() => navigate(`/admin/editor/${encodeURIComponent(form.id)}`)}
                            >
                              Edit
                            </button>
                          )}
                          <button
                            style={{ ...styles.smallOutlineButton, ...getButtonHoverStyle(`published-preview-${form.id}`, { background: '#f5f7fa', borderColor: '#d6dde6', transform: 'translateY(-1px)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`published-preview-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={() => setPreviewForm(form)}
                          >
                            Preview
                          </button>
                          <button
                            style={{ ...styles.smallOutlineButton, ...getButtonHoverStyle(`published-open-${form.id}`, { background: '#f5f7fa', borderColor: '#d6dde6', transform: 'translateY(-1px)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`published-open-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={async () => {
                              const formName = getFormName(form);
                              const lower = (formName || '').toLowerCase();
                              const isRegistration = lower.includes('onboarding') || lower.includes('registration');
                                // Always skipped: this build has no consent step, so asking would
                                // lead to a route that does not exist.
                                const skipConsent = true;
                              sessionStorage.setItem('formReturnPath', window.location.pathname);
                              navigate(buildFormUrl(form.id, formName, { skipConsent }));
                            }}
                          >
                            Open Form
                          </button>
                          <button
                            style={{ ...styles.smallOutlineButton, ...getButtonHoverStyle(`published-rename-${form.id}`, { background: '#f5f7fa', borderColor: '#d6dde6', transform: 'translateY(-1px)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`published-rename-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={() => openRenameModal(form)}
                          >
                            Rename
                          </button>
                          <button
                            style={{ ...styles.smallOutlineButton, ...getButtonHoverStyle(`published-copyfields-${form.id}`, { background: '#f5f7fa', borderColor: '#d6dde6', transform: 'translateY(-1px)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`published-copyfields-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={() => setCopyFieldsSource(form)}
                            title="Copy this template's field configuration onto another template"
                          >
                            Copy Fields
                          </button>
                          <button
                            style={{ ...styles.warningButton, ...getButtonHoverStyle(`published-unpublish-${form.id}`, { background: '#f8fafc', borderColor: 'var(--company-primary-color, #273947)', color: 'var(--company-primary-color, #273947)', transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(39, 55, 71, 0.12)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`published-unpublish-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={() => handleTogglePublish(form)}
                          >
                            Unpublish
                          </button>
                          <button
                            style={{ ...styles.dangerButton, ...getButtonHoverStyle(`published-delete-${form.id}`, { background: '#fff5f5', transform: 'translateY(-1px)' }) }}
                            onMouseEnter={() => setHoveredButtonKey(`published-delete-${form.id}`)}
                            onMouseLeave={() => setHoveredButtonKey(null)}
                            onClick={() => handleDeleteForm(form.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={styles.emptyState}>No published templates</div>
                )
              )}
            </div>

          </>
        )}

        {activeTab === TEMPLATE_TABS.links && (
          <>
            <div style={styles.topInfoRow}>
              <div style={styles.toolbarGroup}>
                <select
                  style={styles.filterSelect}
                  value={linkFormFilter}
                  onChange={(event) => setLinkFormFilter(event.target.value)}
                >
                  <option value="all">All Forms</option>
                  {linkFormOptions.map((option) => (
                    <option key={option.name} value={option.name}>
                      {option.name} ({option.count})
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  style={styles.searchInput}
                  value={linkSearch}
                  onChange={(event) => setLinkSearch(event.target.value)}
                  placeholder="Search links..."
                />
              </div>
              <button style={styles.primaryButton} onClick={() => openGenerateModal()}>
                Generate Link
              </button>
            </div>

            <div style={styles.countText}>{visibleLinks.length} links</div>

            {visibleLinks.length ? (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Template Name</th>
                      <th style={styles.th}>Clinic</th>
                      <th style={styles.th}>Recipient</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Link URL</th>
                      <th style={styles.th}>Created</th>
                      <th style={styles.th}>Expires</th>
                      <th style={styles.th}>Notes</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLinks.map((link) => {
                      const status = getLinkStatus(link);
                      const clinicName =
                        link?.clinicName ||
                        clinicMap[link?.clinicId]?.name ||
                        clinicMap[link?.clinicId]?.code ||
                        '—';

                      const rowKey = link.token || link.url;
                      const shortUrl = (link.url || '').replace(/^https?:\/\/[^/]+/, '');

                      return (
                        <tr
                          key={rowKey}
                          style={hoveredRowKey === rowKey ? styles.tableRowHover : undefined}
                          onMouseEnter={() => setHoveredRowKey(rowKey)}
                          onMouseLeave={() => setHoveredRowKey(null)}
                        >
                          <td style={styles.td}>{link.formName || 'Unknown'}</td>
                          <td style={styles.td}>{clinicName}</td>
                          <td style={styles.td}>{link.doctorEmail || link.patientName || '—'}</td>
                          <td style={styles.td}>
                            <span style={getStatusBadgeStyle(status.type)}>{status.text}</span>
                          </td>
                          <td style={{ ...styles.td, ...styles.linkUrlCell }} title={link.url || ''}>{shortUrl || '—'}</td>
                          <td style={styles.td}>{formatDate(link.createdAt)}</td>
                          <td style={styles.td}>{link.expiresAt ? formatDate(link.expiresAt) : 'Never'}</td>
                          <td style={styles.td}>{link.notes || '—'}</td>
                          <td style={styles.td}>
                            <div style={styles.actionGroup}>
                              <button
                                style={styles.smallPrimaryButton}
                                onClick={async () => {
                                  const text = link.url || '';
                                  const copied = await copyTextToClipboard(text);
                                    // Confirm on success too: silence made a failed copy look identical to a
                                    // successful one, which is how an empty clipboard went unnoticed.
                                    await showAlert(
                                      copied ? 'Link copied to clipboard.' : 'Unable to copy link to clipboard.',
                                      'Copy Link'
                                    );
                                }}
                              >
                                Copy
                              </button>
                              <button style={styles.dangerButton} onClick={() => handleDeleteLink(link.token)}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={styles.emptyState}>
                {linkSearch.trim() || linkFormFilter !== 'all'
                  ? 'No links match the current filter.'
                  : 'No links found. Generate a new link to get started.'}
              </div>
            )}
          </>
        )}

        {isPlatformOwner && activeTab === TEMPLATE_TABS.assign && (
          <>
            <div style={styles.topInfoRow}>
              <div style={styles.countText}>Assign forms to clinics using the matrix below.</div>
              <button style={styles.primaryButton} onClick={saveAssignments} disabled={savingAssignments}>
                {savingAssignments ? 'Saving…' : 'Save Changes'}
              </button>
            </div>

            {publishedForms.length && clinics.length ? (
              <div style={styles.tableWrap}>
                <table style={{ ...styles.table, minWidth: `${Math.max(900, 460 + clinics.length * 140)}px` }}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, width: '28%' }}>Template Name</th>
                      <th style={{ ...styles.th, width: '18%' }}>Owner</th>
                      {clinics.map((clinic) => (
                        <th key={clinic.id} style={{ ...styles.th, textAlign: 'center' }}>
                          {clinic.code || clinic.name || clinic.id}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {publishedForms.map((form) => (
                      <tr key={form.id}>
                        <td style={styles.td}>{getFormName(form)}</td>
                        <td style={styles.td}>
                          <select
                            style={styles.filterSelect}
                            value={form.clinicId || ''}
                            onChange={(event) => handleChangeOwner(form, event.target.value)}
                            title="Which company owns this template. A company can only edit a template it owns."
                          >
                            <option value="">No owner (platform)</option>
                            {clinics.map((clinic) => (
                              <option key={clinic.id} value={clinic.id}>
                                {clinic.code || clinic.name || clinic.id}
                              </option>
                            ))}
                          </select>
                        </td>
                        {clinics.map((clinic) => {
                          const checked = (assignments[clinic.id] || []).includes(form.id);
                          return (
                            <td key={`${clinic.id}-${form.id}`} style={{ ...styles.td, ...styles.assignCell }}>
                              <input
                                type="checkbox"
                                style={styles.matrixCheckbox}
                                checked={checked}
                                onChange={() => handleToggleAssignment(clinic.id, form.id)}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={styles.emptyState}>No published templates are available for clinic assignment yet.</div>
            )}
          </>
        )}

      </div>

      {importModalOpen && (
        <div style={styles.overlay}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()} aria-busy={importing}>
            <div style={importing ? { opacity: 0.45, pointerEvents: 'none', userSelect: 'none' } : undefined}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>{isSmartImport ? 'Smart Import Form Template' : 'Import New Form Template'}</h2>
                <button style={styles.closeButton} onClick={closeImportModal} disabled={importing} aria-label="Close import dialog">×</button>
              </div>

              <div
                style={{ ...styles.dropZone, ...(dragOver ? styles.dropZoneActive : {}), ...(importing ? { cursor: 'not-allowed' } : {}) }}
                aria-disabled={importing}
                onClick={() => {
                  if (!importing) fileInputRef.current?.click();
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  if (importing) return;
                  setDragOver(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (importing) return;
                  setDragOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  if (importing) return;
                  setDragOver(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (importing) return;
                  setDragOver(false);
                  handleSelectFiles(e.dataTransfer.files);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  multiple
                  style={{ display: 'none' }}
                  disabled={importing}
                  onChange={(e) => handleSelectFiles(e.target.files)}
                />
                <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Drag & Drop PDF here</div>
                <div style={styles.helper}>or click to browse files (.pdf, .doc, .docx)</div>
              </div>

              {selectedFiles.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={styles.label}>Selected Files:</div>
                  <div style={styles.helper}>
                    {selectedFiles.map((file) => file.name).join(', ')}
                  </div>
                </div>
              )}
            </div>

            {importItems.length > 0 && (
              <div style={{ marginBottom: '16px' }} aria-live="polite" aria-atomic="false">
                <div style={{ ...styles.label, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span>{importing ? 'Import in progress' : 'Import finished'}</span>
                  <span>{importElapsed}s elapsed</span>
                </div>
                <div style={styles.helper}>
                  {importItems.filter(item => item.status === 'completed').length} completed · {importItems.filter(item => item.status === 'failed').length} failed · {importItems.filter(item => item.status === 'queued').length} waiting / {importItems.length} files
                </div>
                {importItems.filter(item => item.status === 'active').map(item => (
                  <div key={item.name} style={{ margin: '12px 0', overflowWrap: 'anywhere' }}>
                    <strong>{item.name}</strong><br />
                    {item.stage}{item.currentPage != null && ` · Page ${item.currentPage}${item.totalPages ? ` / ${item.totalPages}` : ''}`}
                  </div>
                ))}
                <div style={styles.progressBarWrap} role="progressbar" aria-label="Files processed" aria-valuemin={0} aria-valuemax={100} aria-valuenow={importProgress}>
                  <div style={{ ...styles.progressBar, width: `${importProgress}%` }} />
                </div>
                <div style={{ ...styles.helper, marginBottom: 12 }}>Files processed: {importProgress}%{importing ? ' — AI analysis may take several minutes.' : ''}</div>
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {importItems.map((item, i) => (
                    <div key={`${i}-${item.name}`} style={{ padding: '10px 0', borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
                      <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{i + 1}. {item.name}</div>
                      <div style={styles.helper}>
                        {item.status === 'completed' ? 'Completed' : item.status === 'failed' ? 'Failed' : item.status === 'queued' ? 'Waiting' : 'Processing'} — {item.stage}
                        {item.currentPage != null && ` · Page ${item.currentPage}${item.totalPages ? ` / ${item.totalPages}` : ''}`}
                        {item.fieldsFound != null && ` · ${item.fieldsFound} fields detected`}
                      </div>
                      {item.warning && <div style={styles.helper}>{item.warning}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ ...styles.modalFooter, ...(importing ? { opacity: 0.45, pointerEvents: 'none', userSelect: 'none' } : {}) }}>
              <button style={styles.outlineButton} onClick={closeImportModal} disabled={importing}>
                Cancel
              </button>
              <button style={styles.primaryButton} onClick={uploadFiles} disabled={importing || !selectedFiles.length}>
                Upload & Create Draft
              </button>
            </div>
          </div>
        </div>
      )}

      {generateModalOpen && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Generate Form Link</h2>
              <button style={styles.closeButton} onClick={closeGenerateModal}>×</button>
            </div>

            {selectedGenerateForm && (
              <div style={{ marginBottom: '16px', fontWeight: 600, color: '#0d8a8a' }}>
                Template: {getFormName(selectedGenerateForm)}
              </div>
            )}

            {!selectedGenerateForm && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Select Template</label>
                <select
                  style={styles.input}
                  value={generateForm.formId}
                  onChange={(e) => setGenerateForm((prev) => ({ ...prev, formId: e.target.value }))}
                >
                  <option value="">-- Select Template --</option>
                  {templateOptions.map((form) => (
                    <option key={form.id} value={form.id}>{form.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Form Recipient Email <span style={{ color: '#dc3545' }}>*</span></label>
              <input
                type="email"
                style={styles.input}
                list="doctor-email-options"
                value={generateForm.doctorEmail}
                onChange={(e) => setGenerateForm((prev) => ({ ...prev, doctorEmail: e.target.value }))}
                placeholder="e.g. doctor@example.com"
              />
              <datalist id="doctor-email-options">
                {doctorEmails.map((email) => (
                  <option key={email} value={email} />
                ))}
              </datalist>
              <div style={styles.helper}>Submission of this form will be sent to this email.</div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>CC Email</label>
              <input
                type="email"
                multiple
                style={styles.input}
                value={generateForm.ccEmail}
                onChange={(e) => setGenerateForm((prev) => ({ ...prev, ccEmail: e.target.value }))}
                placeholder="e.g. recep@example.com"
              />
              <div style={styles.helper}>Copied on the submission email and visible to every recipient. Separate multiple addresses with a comma.</div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>BCC Email</label>
              <input
                type="email"
                multiple
                style={styles.input}
                value={generateForm.bccEmail}
                onChange={(e) => setGenerateForm((prev) => ({ ...prev, bccEmail: e.target.value }))}
                placeholder="e.g. records@example.com"
              />
              <div style={styles.helper}>Copied on the submission email but hidden from the other recipients. Separate multiple addresses with a comma.</div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Link Expiry Date <span style={{ color: '#dc3545' }}>*</span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="date"
                  style={{ ...styles.input, flex: 1, opacity: generateForm.neverExpired ? 0.5 : 1 }}
                  value={generateForm.expiryDate}
                  min={hkDateISO()}
                  onChange={(e) => setGenerateForm((prev) => ({ ...prev, expiryDate: e.target.value }))}
                  disabled={generateForm.neverExpired}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#374151', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={generateForm.neverExpired}
                    onChange={(e) => setGenerateForm((prev) => ({ ...prev, neverExpired: e.target.checked, expiryDate: e.target.checked ? '' : prev.expiryDate }))}
                  />
                  Never Expired
                </label>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Link Type <span style={{ color: '#dc3545' }}>*</span></label>
              <select
                style={styles.input}
                value={generateForm.linkType}
                onChange={(e) => setGenerateForm((prev) => ({ ...prev, linkType: e.target.value }))}
              >
                <option value="reusable">Reusable Link (Allow submissions by multiple patient)</option>
                <option value="single">One-Off Link (Single patient only)</option>
              </select>
            </div>


            {/* Optional pre-fill. Staff open the real form, fill in whatever the
                patient should not have to retype, and save; those answers are
                attached to the link so the patient sees them already filled in.
                */}
            {generateForm.formId !== '__heidi_consent' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Pre-fill <span style={{ color: '#6b7280', fontWeight: 400 }}>(optional)</span></label>
                {!generateForm.formId ? (
                  <div style={styles.helper}>Select a template first to pre-fill it.</div>
                ) : prefillCount > 0 ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: '8px', padding: '10px 12px', background: '#e6f5f5',
                    border: '1px solid #9fd4d4', borderRadius: '6px',
                  }}>
                    <span style={{ fontSize: '14px', color: '#0b6b6b', fontWeight: 500 }}>
                      {prefillCount} field{prefillCount === 1 ? '' : 's'} pre-filled
                    </span>
                    <span>
                      <button
                        type="button"
                        style={{ background: 'none', border: 'none', color: '#0d8a8a', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline', padding: '0 6px' }}
                        onClick={() => handleFillFormFirst('edit')}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={{ background: 'none', border: 'none', color: '#0d8a8a', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline', padding: '0 6px' }}
                        onClick={clearPrefill}
                      >
                        Clear
                      </button>
                    </span>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      style={{ padding: '8px 16px', background: '#fff', color: '#0d8a8a', border: '1px solid #0d8a8a', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, fontSize: '14px' }}
                      onClick={() => handleFillFormFirst('new')}
                    >
                      Pre-fill Form
                    </button>
                    <div style={styles.helper}>
                      Pre-fill fields on the form if needed. Your entries will be saved
                      and shown to the patient when they open the link.
                    </div>
                  </>
                )}
              </div>
            )}

            {generateResult && (
              <div style={styles.resultBox}>
                <div style={styles.label}>Generated Link:</div>
                <div style={styles.resultRow}>
                  <input style={styles.monoInput} readOnly value={generateResult.url || ''} />
                  <button
                    style={styles.primaryButton}
                    onClick={async () => {
                      const text = generateResult.url || '';
                      const copied = await copyTextToClipboard(text);
                        // Confirm on success too: silence made a failed copy look identical to a
                        // successful one, which is how an empty clipboard went unnoticed.
                        await showAlert(
                          copied ? 'Link copied to clipboard.' : 'Unable to copy link to clipboard.',
                          'Copy Link'
                        );
                    }}
                  >
                    Copy
                  </button>
                </div>
                <div style={styles.helper}>
                  {generateResult.expiresAt ? `Expires: ${formatDate(generateResult.expiresAt)}` : 'This link never expires.'}
                </div>
              </div>
            )}

            <div style={styles.modalFooter}>
              {generateResult ? (
                <>
                  <button style={styles.outlineButton} onClick={resetGenerateModal}>Generate Another</button>
                  <button style={styles.primaryButton} onClick={closeGenerateModal}>Done</button>
                </>
              ) : (
                <>
                  <button style={styles.outlineButton} onClick={closeGenerateModal}>Cancel</button>
                  <button style={styles.primaryButton} onClick={handleGenerateLink} disabled={generateSubmitting}>
                    {generateSubmitting ? 'Generating…' : 'Generate Link'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {copyFieldsSource && (
        <CopyFieldsModal
          sourceForm={copyFieldsSource}
          allForms={forms}
          onConfirm={handleCopyFields}
          onCancel={() => setCopyFieldsSource(null)}
        />
      )}

      {renameModalOpen && (
        <div style={styles.overlay}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Rename Template</h2>
              <button style={styles.closeButton} onClick={closeRenameModal}>×</button>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Template Name</label>
              <input
                style={styles.input}
                value={renameFormState.name}
                onChange={(event) => setRenameFormState((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Enter template name"
              />
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.outlineButton} onClick={closeRenameModal} disabled={renameSubmitting}>
                Cancel
              </button>
              <button style={styles.primaryButton} onClick={handleRenameForm} disabled={renameSubmitting}>
                {renameSubmitting ? 'Saving…' : 'Save Name'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewForm && (
        <div style={styles.overlay} onClick={() => setPreviewForm(null)}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 0, width: '90%', maxWidth: 900, height: '85vh', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #e9ecef', background: '#f8f9fa' }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--company-primary-color, #273947)', margin: 0 }}>{getFormName(previewForm)}</h3>
              <button style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', padding: '4px 8px', lineHeight: 1 }} onClick={() => setPreviewForm(null)}>✕</button>
            </div>
            <iframe
              src={buildFormUrl(previewForm.id, getFormName(previewForm)).startsWith('/onboarding')
                ? buildFormUrl(previewForm.id, getFormName(previewForm))
                : `${getApiBaseUrl()}/admin/forms/${encodeURIComponent(previewForm.id)}/preview`}
              style={{ flex: 1, border: 'none', width: '100%' }}
              title="Template Preview"
            />
          </div>
        </div>
      )}

    </div>
  );
}
